import { PLACEMENTS, type InletDesign, type InletUsage } from "../model/inp/inlets.ts";
import { useT } from "../i18n/hooks.ts";
import { NumberInput } from "./fields.tsx";
import { TrashIcon } from "./TrashIcon.tsx";

/** [INLET_USAGE] for one conduit. A link can carry more than one row — this app's stand-in for a combo inlet, since a true single merged COMBO design isn't modeled (see inlets.ts). */
export function InletUsageEditor({
  linkName,
  inletDesigns,
  usage,
  nodeNames,
  onChange,
}: {
  linkName: string;
  inletDesigns: InletDesign[];
  usage: InletUsage[];
  nodeNames: string[];
  onChange: (rows: InletUsage[]) => void;
}) {
  const t = useT();

  function addRow() {
    if (inletDesigns.length === 0) return;
    onChange([
      ...usage,
      {
        link: linkName,
        inlet: inletDesigns[0].name,
        node: nodeNames[0] ?? "",
        numInlets: 1,
        pctClogged: 0,
        flowLimit: 0,
        localDepress: 0,
        localWidth: 0,
        placement: "AUTOMATIC",
      },
    ]);
  }

  function patchAt(index: number, fields: Partial<InletUsage>) {
    onChange(usage.map((row, i) => (i === index ? { ...row, ...fields } : row)));
  }

  function removeAt(index: number) {
    onChange(usage.filter((_, i) => i !== index));
  }

  if (inletDesigns.length === 0) {
    return (
      <section className="property-panel-group">
        <h3>{t("propertyPanel.inletUsage")}</h3>
        <p className="property-panel-hint">{t("propertyPanel.noInletDesignsYet")}</p>
      </section>
    );
  }

  return (
    <section className="property-panel-group">
      <h3>{t("propertyPanel.inletUsage")}</h3>
      {usage.length === 0 && (
        <p className="property-panel-hint">{t("propertyPanel.noInletUsage")}</p>
      )}
      {usage.length > 0 && (
        <div className="named-collection-points-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th>{t("propertyPanel.inletUsageDesign")}</th>
                <th>{t("propertyPanel.inletUsageNode")}</th>
                <th>{t("propertyPanel.inletUsageNumInlets")}</th>
                <th>{t("propertyPanel.inletUsagePctClogged")}</th>
                <th>{t("propertyPanel.inletUsageFlowLimit")}</th>
                <th>{t("propertyPanel.inletUsageLocalDepress")}</th>
                <th>{t("propertyPanel.inletUsageLocalWidth")}</th>
                <th>{t("propertyPanel.inletUsagePlacement")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {usage.map((row, index) => (
                <tr key={index}>
                  <td>
                    <select
                      value={row.inlet}
                      onChange={(e) => patchAt(index, { inlet: e.target.value })}
                    >
                      {inletDesigns.map((d) => (
                        <option key={d.name} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={row.node}
                      onChange={(e) => patchAt(index, { node: e.target.value })}
                    >
                      {nodeNames.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <NumberInput
                      value={row.numInlets}
                      onChange={(v) => patchAt(index, { numInlets: v })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={row.pctClogged}
                      onChange={(v) => patchAt(index, { pctClogged: v })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={row.flowLimit}
                      onChange={(v) => patchAt(index, { flowLimit: v })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={row.localDepress}
                      onChange={(v) => patchAt(index, { localDepress: v })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={row.localWidth}
                      onChange={(v) => patchAt(index, { localWidth: v })}
                    />
                  </td>
                  <td>
                    <select
                      value={row.placement}
                      onChange={(e) => patchAt(index, { placement: e.target.value as never })}
                    >
                      {PLACEMENTS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button-icon button-icon-danger"
                      onClick={() => removeAt(index)}
                      aria-label={t("propertyPanel.deleteInletUsage")}
                      title={t("propertyPanel.deleteInletUsage")}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button className="button" onClick={addRow}>
        {t("propertyPanel.addInletUsage")}
      </button>
    </section>
  );
}
